import { createContext, FC, useCallback, useContext, useMemo } from 'react';
import { toastService } from 'services';
import { useGQLMutation } from 'hooks/useGQLMutation.ts';
import {
  EnableOvertimeStrategyEnum,
  Mutation_ManuallyApproveCalloutMutation as MUTATION_manuallyApproveCallout_type,
  Mutation_ManuallyApproveCalloutMutationVariables,
} from 'types/graphql';
import { MUTATION_manuallyApproveCallout } from 'mutation';
import { UseGQLMutation } from 'hooks/useGQLMutation.ts/useGQLMutation.types';
import { ContinueCalloutDialogProps } from 'components/ContinueCalloutDialog/ContinueCalloutDialog.types';
import { useTranslation } from 'hooks/useTranslation';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';
import { useFormBehaviors } from 'hooks';
import { UseFormBehaviors } from 'hooks/useFormBehaviors/useFormBehaviors.types';
import { useContinueCalloutDialogDataContext } from 'components/ContinueCalloutDialog/ContinueCalloutDialog.data.context';

interface ContinueCalloutDialogForm
  extends Omit<
    UseGQLMutation<
      MUTATION_manuallyApproveCallout_type,
      Mutation_ManuallyApproveCalloutMutationVariables
    >,
    'mutate'
  > {
  handleContinueCallout: () => void;
}

export interface IContinueCalloutDialogEnableOvertimeForm {
  allowOvertime: boolean;
  withdrawShifts: boolean;
}

export const ContinueCalloutDialogFormContext = createContext(
  {} as ContinueCalloutDialogForm & UseFormBehaviors<IContinueCalloutDialogEnableOvertimeForm>,
);

export const ContinueCalloutDialogFormProvider: FC<ContinueCalloutDialogProps> = ({
  children,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { calloutListId } = useContinueCalloutDialogDataContext();
  const form = useFormBehaviors<IContinueCalloutDialogEnableOvertimeForm>({
    initialState: {
      allowOvertime: false,
      withdrawShifts: false,
    },
  });
  const enableOvertimeStrategy = form.values.withdrawShifts
    ? EnableOvertimeStrategyEnum.NO_RESTART_WITHDRAW
    : EnableOvertimeStrategyEnum.NO_RESTART;
  const enableOvertime = form.values.allowOvertime ? enableOvertimeStrategy : null;
  const { mutate, ...rest } = useGQLMutation<
    MUTATION_manuallyApproveCallout_type,
    Mutation_ManuallyApproveCalloutMutationVariables
  >({
    key: 'MANUALLY_APPROVE_CALLOUT_CONTINUE',
    mutation: MUTATION_manuallyApproveCallout,
    variables: {
      calloutListId,
      enableOvertime,
    },
    refetchQueries: ['shiftModalOrderDetails'],
    onSuccess: (res) => {
      if (res?.approveCallout?.errors && !!res?.approveCallout?.errors?.length)
        return toastService.error(t('errors:UNABLE_TO_CONTINUE_CALL_OUT'));
      return onSuccess();
    },
    onError: handleGenericErrorMessage,
  });

  const handleContinueCallout = useCallback(async () => {
    await mutate({ variables: { calloutListId, enableOvertime } });
  }, [calloutListId, enableOvertime, mutate]);

  const contextValue = useMemo(
    () => ({
      handleContinueCallout,
      ...rest,
      ...form,
    }),
    [form, handleContinueCallout, rest],
  );

  return (
    <ContinueCalloutDialogFormContext.Provider value={contextValue}>
      {children}
    </ContinueCalloutDialogFormContext.Provider>
  );
};

export const useContinueCalloutDialogFormContext = () =>
  useContext(ContinueCalloutDialogFormContext);
